import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StaffLayout } from "@/components/StaffLayout";
import { StatusPill } from "@/components/StatusPill";
import { PasswordCard } from "@/components/PasswordCard";
import { Button } from "@/components/ui/button";

const ROLES = ["client", "investigator", "staff", "admin"];

export default function StaffAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/audit").then(({ data }) => setAudit(data)).catch(() => {});
    api.get("/arkcase/sync").then(({ data }) => setSync(data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/arkcase/sync");
      setSync(data);
      toast.success("Evidence sync pass complete", { description: data.last_result });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const setRole = async (target, role) => {
    try {
      await api.patch(`/users/${target.id}/role`, { role });
      toast.success(`${target.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <StaffLayout>
      <div>
        <p className="text-xs uppercase tracking-[0.28em] text-gold">Governance</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl text-[#F3F4F6]">Access & audit</h1>
        <p className="mt-3 text-sm text-[#9CA3AF]">
          Roles mirror ArkCase authorities. {isAdmin ? "As an admin you can override them here." : "Only admins can change roles."}
        </p>
      </div>

      <div className="glass rounded-2xl overflow-hidden divide-y divide-white/5" data-testid="users-table">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 px-6 py-4" data-testid="user-row">
            <div className="min-w-[220px] flex-1">
              <p className="text-sm text-[#F3F4F6]">{u.name}</p>
              <p className="text-xs text-[#6B7280]">
                {u.email} · {u.title || "—"} · source {u.source}
              </p>
            </div>
            <StatusPill value={u.role} label={u.role} testId="user-role-pill" />
            <select
              data-testid="user-role-select"
              disabled={!isAdmin}
              value={u.role}
              onChange={(e) => setRole(u, e.target.value)}
              className="rounded-full bg-white/5 border border-white/15 px-3 py-1.5 text-xs text-[#F3F4F6] disabled:opacity-40"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {user?.source === "portal-admin" ? <PasswordCard /> : null}

      <div className="glass rounded-2xl p-7" data-testid="sync-panel">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <h2 className="font-display text-xl text-[#F3F4F6]">ArkCase evidence sync</h2>
            <p className="mt-2 text-sm text-[#9CA3AF]">
              Background worker forwards client uploads to ArkCase every {sync?.interval_seconds ?? 60}s.
            </p>
          </div>
          <Button
            data-testid="sync-now-btn"
            disabled={syncing}
            onClick={runSync}
            className="ml-auto rounded-full bg-gold text-navy hover:bg-gold-dark"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
        {sync ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Queued", sync.pending],
              ["Forwarded", sync.synced_total],
              ["Failed", sync.failed],
              ["Worker", sync.running ? "running" : "stopped"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                <p className="font-display text-2xl text-[#F3F4F6]">{v}</p>
                <p className="mt-1 text-[11px] uppercase tracking-widest text-[#6B7280]">{k}</p>
              </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center gap-4 text-xs text-[#6B7280]">
              <StatusPill
                value={sync.arkcase?.available ? "completed" : "overdue"}
                label={sync.arkcase?.available ? "ArkCase connected" : "ArkCase unreachable"}
                testId="sync-arkcase-pill"
              />
              <span>Last pass {dateTime(sync.last_run)}</span>
              <span>{sync.last_result || "—"}</span>
              {sync.arkcase?.error ? <span className="text-red-300">{sync.arkcase.error}</span> : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="glass rounded-2xl p-7">
        <h2 className="font-display text-xl text-[#F3F4F6]">Audit log</h2>
        <div className="mt-5 space-y-3 max-h-[420px] overflow-y-auto scroll-thin pr-2" data-testid="audit-log">
          {audit.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 border-b border-white/5 pb-3 text-sm">
              <span className="text-gold text-xs uppercase tracking-wider min-w-[120px]">{a.action}</span>
              <span className="text-[#D1D5DB]">{a.target}</span>
              {a.detail ? <span className="text-xs text-[#6B7280]">({a.detail})</span> : null}
              <span className="ml-auto text-xs text-[#6B7280]">
                {a.actor_email} · {dateTime(a.created_at)}
              </span>
            </div>
          ))}
          {!audit.length ? <p className="text-sm text-[#9CA3AF]">No activity recorded yet.</p> : null}
        </div>
      </div>
    </StaffLayout>
  );
}
